import { useMemo } from 'react';
import { Roles } from 'modules/core/enums';
import { SSO_TYPE } from 'modules/core/constants';
import { IUser } from 'modules/core/types';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { ILenderState } from 'store/reducers/lenders.reducer';

interface ILenderMode {
  isWithoutSSO: boolean;
  isDealerSsoType: boolean;
  lenderHasRouteOneConfig: boolean;
  hasValidDealSourceIncome: boolean;
  isShopByBudgetMode: boolean;
  isShopByCallbackMode: boolean;
  isRouteOneFlowForSbbOrSbc: boolean;
  isDealerSsoSbbOrSbcFlow: boolean;
  isMakeItWork: boolean;
  isSubmitR1DealBlockedByControls: boolean;
}

export const useLenderMode = (
  user: IUser | null,
  lenderState: ILenderState,
  dealSourceData?: IDealSourceData,
): ILenderMode => {
  const isSalesperson = user?.role === Roles.SALESPERSON;
  const isWithoutSSO = !user?.is_sso && !!lenderState?.route_one_app && !!lenderState?.route_one_id;
  const isDealerSsoType = user?.sso_type === SSO_TYPE.DEALER;
  const lenderHasRouteOneConfig = !!lenderState?.route_one_app && !!lenderState?.route_one_id;

  const hasValidDealSourceIncome = useMemo(() => {
    const income = dealSourceData?.income;

    if (!income) return false;

    return Boolean(
      (income.primary_monthly_income && income.primary_monthly_income > 0) ||
        (income.primary_other_monthly_income && income.primary_other_monthly_income > 0) ||
        (income.coApplicant_monthly_income && income.coApplicant_monthly_income > 0) ||
        (income.coApplicant_other_monthly_income && income.coApplicant_other_monthly_income > 0),
    );
  }, [dealSourceData?.income]);

  const isShopByBudgetMode = !!lenderState?.isShopByBudgetEnabled && !lenderState?.isShopByCallbackEnabled;

  const isShopByCallbackMode =
    !!lenderState?.isShopByCallbackEnabled && !lenderState?.isShopByBudgetEnabled && !lenderState?.route_one_id;

  const isRouteOneFlowForSbbOrSbc = (isShopByBudgetMode || isShopByCallbackMode) && !!lenderState?.route_one_app;

  // Same lender test as the backend's is_sbb_plus_lender / is_sbc_plus_lender.
  const isSbbOrSbcPlusLender =
    (!!lenderState?.isShopByBudgetEnabled || !!lenderState?.isShopByCallbackEnabled) && !lenderState?.route_one_id;

  const isDealerSsoSbbOrSbcFlow = !!user?.is_sso && isDealerSsoType && isSbbOrSbcPlusLender;

  const isMakeItWork =
    (user?.is_sso && dealSourceData?.lender_path === window.location.pathname) ||
    ((isWithoutSSO || isRouteOneFlowForSbbOrSbc) && !isSalesperson) ||
    (isDealerSsoType && lenderHasRouteOneConfig && !isRouteOneFlowForSbbOrSbc && !isSalesperson);

  const isSubmitR1DealBlockedByControls =
    isMakeItWork &&
    (!user?.is_sso ||
      user?.isAlternativeSso ||
      (!!user?.originalExternalLenderId && !lenderState?.route_one_id?.startsWith(user.originalExternalLenderId))) &&
    !!lenderState?.route_one_disable_submit;

  return {
    isWithoutSSO,
    isDealerSsoType,
    lenderHasRouteOneConfig,
    hasValidDealSourceIncome,
    isShopByBudgetMode,
    isShopByCallbackMode,
    isRouteOneFlowForSbbOrSbc,
    isDealerSsoSbbOrSbcFlow,
    isMakeItWork,
    isSubmitR1DealBlockedByControls,
  };
};
